import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  DollarSign, Calendar, Users, TrendingUp, MessageSquare, ChevronRight, 
  ClipboardList, CheckCircle, AlertCircle, FileText, PenTool, Mail, Plus, 
  MapPin, ChevronDown, ChevronUp, GripVertical, LayoutGrid, CalendarDays,
  List, Settings, X, Eye, EyeOff
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { useSettings } from '@/contexts/SettingsContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default widget configuration
const DEFAULT_WIDGETS = [
  { id: 'stats', title: 'Quick Stats', visible: true, collapsed: false },
  { id: 'upcoming-events', title: 'Upcoming Events', visible: true, collapsed: false },
  { id: 'pending-contracts', title: 'Contracts Pending', visible: true, collapsed: false },
  { id: 'unread-emails', title: 'Unread Emails', visible: true, collapsed: false },
  { id: 'addon-requests', title: 'Add-on Requests', visible: true, collapsed: false },
  { id: 'venue-changes', title: 'Venue Changes', visible: true, collapsed: false },
  { id: 'tasks', title: 'Tasks Due', visible: true, collapsed: false },
  { id: 'unread-messages', title: 'Unread Messages', visible: true, collapsed: false },
];

// Sortable Widget Wrapper Component
function SortableWidget({ id, children, isCustomizing }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {isCustomizing && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-lg bg-white/10 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-4 h-4 text-white/60" />
        </div>
      )}
      {children}
    </div>
  );
}

// Collapsible Card Component
function CollapsibleCard({ title, icon: Icon, iconColor, badge, collapsed, onToggle, onHide, isCustomizing, children, testId }) {
  return (
    <Card className="glass-card border-glass-border" data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between cursor-pointer" onClick={onToggle}>
        <CardTitle className="font-heading text-2xl flex items-center gap-2">
          {Icon && <Icon className={`w-6 h-6 ${iconColor}`} />}
          {title}
          {badge > 0 && (
            <span className={`ml-2 px-2 py-0.5 text-white text-sm rounded-full ${iconColor?.includes('orange') ? 'bg-orange-500' : iconColor?.includes('blue') ? 'bg-blue-500' : iconColor?.includes('purple') ? 'bg-purple-500' : iconColor?.includes('emerald') ? 'bg-emerald-500' : iconColor?.includes('red') ? 'bg-red-500' : 'bg-neon-blue'}`}>
              {badge}
            </span>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          {isCustomizing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onHide(); }}
              className="text-muted-foreground hover:text-red-400"
              title="Hide widget"
            >
              <EyeOff className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            {collapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </Button>
        </div>
      </CardHeader>
      {!collapsed && <CardContent>{children}</CardContent>}
    </Card>
  );
}

// Calendar View Component
function CalendarView({ events, formatDate, formatCurrency, navigate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add all days in the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date || !events) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.event_date === dateStr);
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
        >
          &larr; Prev
        </Button>
        <h3 className="text-xl font-semibold">{monthName}</h3>
        <Button
          variant="ghost"
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
        >
          Next &rarr;
        </Button>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}
        {days.map((day, idx) => {
          const dayEvents = getEventsForDate(day);
          const isToday = day && day.toDateString() === new Date().toDateString();
          
          return (
            <div
              key={idx}
              className={`min-h-[80px] p-1 rounded-lg border transition-colors ${
                day ? 'border-white/10 hover:border-white/30' : 'border-transparent'
              } ${isToday ? 'bg-neon-blue/20 border-neon-blue/50' : ''}`}
            >
              {day && (
                <>
                  <div className={`text-sm font-medium ${isToday ? 'text-neon-blue' : 'text-muted-foreground'}`}>
                    {day.getDate()}
                  </div>
                  <div className="space-y-1 mt-1">
                    {dayEvents.slice(0, 2).map(event => (
                      <div
                        key={event.id}
                        onClick={() => navigate(`/bookings?open=${event.id}`)}
                        className="text-xs p-1 rounded bg-neon-purple/30 text-white truncate cursor-pointer hover:bg-neon-purple/50"
                        title={`${event.client_name} - ${event.event_type}`}
                      >
                        {event.client_name}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-muted-foreground">+{dayEvents.length - 2} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [unreadEmails, setUnreadEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [pendingAddonRequests, setPendingAddonRequests] = useState([]);
  const [loadingAddonRequests, setLoadingAddonRequests] = useState(true);
  const [pendingVenueChanges, setPendingVenueChanges] = useState([]);
  const [loadingVenueChanges, setLoadingVenueChanges] = useState(true);
  const [tasksData, setTasksData] = useState({ tasks: [], overdue_count: 0, due_today_count: 0 });
  const [loadingTasks, setLoadingTasks] = useState(true);
  
  // Dashboard customization state
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboard_widgets');
    return saved ? JSON.parse(saved) : DEFAULT_WIDGETS;
  });
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('dashboard_view') || 'list';
  });
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  
  const navigate = useNavigate();
  const { formatDate } = useSettings();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchStats();
    fetchUnreadMessages();
    fetchUnreadEmails();
    fetchPendingContracts();
    fetchPendingAddonRequests();
    fetchPendingVenueChanges();
    fetchTasks();
    fetchAllEvents();
  }, []);

  // Save preferences when widgets change
  useEffect(() => {
    localStorage.setItem('dashboard_widgets', JSON.stringify(widgets));
  }, [widgets]);

  useEffect(() => {
    localStorage.setItem('dashboard_view', viewMode);
  }, [viewMode]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEvents = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      const upcomingEvents = (response.data || []).filter(b => {
        const eventDate = new Date(b.event_date);
        return eventDate >= new Date() && b.status !== 'cancelled';
      });
      setAllEvents(upcomingEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks/due`);
      setTasksData(response.data || { tasks: [], overdue_count: 0, due_today_count: 0 });
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread`);
      setUnreadMessages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchUnreadEmails = async () => {
    try {
      const response = await axios.get(`${API}/communications/unread`);
      setUnreadEmails(response.data || []);
    } catch (error) {
      console.error('Failed to fetch unread emails:', error);
    } finally {
      setLoadingEmails(false);
    }
  };

  const fetchPendingContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts/pending-signature`);
      setPendingContracts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending contracts:', error);
    } finally {
      setLoadingContracts(false);
    }
  };

  const fetchPendingAddonRequests = async () => {
    try {
      const response = await axios.get(`${API}/addon-requests/pending`);
      setPendingAddonRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending addon requests:', error);
    } finally {
      setLoadingAddonRequests(false);
    }
  };

  const fetchPendingVenueChanges = async () => {
    try {
      const response = await axios.get(`${API}/venue-change-requests/pending`);
      setPendingVenueChanges(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending venue changes:', error);
    } finally {
      setLoadingVenueChanges(false);
    }
  };

  const handleEmailClick = (email) => {
    if (email.booking_id) {
      navigate(`/bookings?booking=${email.booking_id}&tab=communications`);
    }
  };

  const handleAddonRequestClick = (request) => {
    if (request.booking_id) {
      navigate(`/bookings?booking=${request.booking_id}&tab=addon-requests`);
    }
  };

  const handleVenueChangeClick = (request) => {
    if (request.booking_id) {
      navigate(`/bookings?booking=${request.booking_id}&tab=venue-changes`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Widget handlers
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const toggleWidgetCollapse = (widgetId) => {
    setWidgets(widgets.map(w => 
      w.id === widgetId ? { ...w, collapsed: !w.collapsed } : w
    ));
  };

  const toggleWidgetVisibility = (widgetId) => {
    setWidgets(widgets.map(w => 
      w.id === widgetId ? { ...w, visible: !w.visible } : w
    ));
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    localStorage.removeItem('dashboard_widgets');
    toast.success('Dashboard reset to default');
  };

  // Render widget content based on ID
  const renderWidgetContent = (widgetId) => {
    switch (widgetId) {
      case 'stats':
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Calendar} iconColor="text-neon-purple" label="Upcoming Events" value={stats?.upcoming_count || 0} />
            <StatCard icon={Users} iconColor="text-neon-blue" label="Total Leads" value={stats?.total_leads || 0} />
            <StatCard icon={DollarSign} iconColor="text-neon-blue" label="Total Revenue" value={formatCurrency(stats?.total_revenue || 0)} />
            <StatCard icon={TrendingUp} iconColor="text-neon-pink" label="Pending Revenue" value={formatCurrency(stats?.pending_revenue || 0)} />
          </div>
        );

      case 'upcoming-events':
        if (viewMode === 'calendar') {
          return <CalendarView events={allEvents} formatDate={formatDate} formatCurrency={formatCurrency} navigate={navigate} />;
        }
        return stats?.upcoming_events && stats.upcoming_events.length > 0 ? (
          <div className="space-y-4">
            {stats.upcoming_events.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                data-testid={`upcoming-event-${event.id}`}
              >
                <div>
                  <h3 className="font-semibold">{event.client_name}</h3>
                  <p className="text-sm text-muted-foreground">{event.event_type} at {event.venue}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-semibold text-neon-blue">{formatDate(event.event_date)}</div>
                    <div className="text-sm text-muted-foreground">{formatCurrency(event.total_amount)}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/bookings?open=${event.id}`)}
                    className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Manage
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No upcoming events</p>
        );

      case 'pending-contracts':
        return loadingContracts ? (
          <p className="text-center text-muted-foreground py-4">Loading...</p>
        ) : pendingContracts.length > 0 ? (
          <div className="space-y-3">
            {pendingContracts.slice(0, 5).map((contract) => (
              <div
                key={contract.id}
                className="flex items-start gap-4 p-4 rounded-xl bg-orange-500/10 hover:bg-orange-500/20 transition-colors cursor-pointer"
                onClick={() => navigate(`/bookings?open=${contract.booking_id}&tab=contracts`)}
              >
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <PenTool className="w-5 h-5 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate">{contract.client_name || 'Unknown Client'}</h4>
                  <p className="text-sm text-muted-foreground">{contract.event_type} - {formatDate(contract.event_date)}</p>
                  <p className="text-xs text-orange-400 mt-1">
                    {contract.client_signed_at ? 'Awaiting your signature' : 'Awaiting client signature'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
            <p className="text-muted-foreground">All contracts signed!</p>
          </div>
        );

      case 'unread-emails':
        return loadingEmails ? (
          <p className="text-center text-muted-foreground py-4">Loading...</p>
        ) : unreadEmails.length > 0 ? (
          <div className="space-y-3">
            {unreadEmails.slice(0, 5).map((email) => (
              <div
                key={email.id}
                className="flex items-start gap-4 p-4 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 transition-colors cursor-pointer border-l-4 border-blue-500"
                onClick={() => handleEmailClick(email)}
              >
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate">{email.subject}</h4>
                  <p className="text-sm text-muted-foreground truncate">{email.client_name || 'Unknown'}</p>
                  <p className="text-xs text-muted-foreground">{formatTimeAgo(email.created_at)}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Mail className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No unread emails</p>
          </div>
        );

      case 'addon-requests':
        return loadingAddonRequests ? (
          <p className="text-center text-muted-foreground py-4">Loading...</p>
        ) : pendingAddonRequests.length > 0 ? (
          <div className="space-y-3">
            {pendingAddonRequests.slice(0, 5).map((request) => (
              <div
                key={request.id}
                className="flex items-start gap-4 p-4 rounded-xl bg-purple-500/10 hover:bg-purple-500/20 transition-colors cursor-pointer border-l-4 border-purple-500"
                onClick={() => handleAddonRequestClick(request)}
              >
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate">{request.addon_name}</h4>
                  <p className="text-sm text-muted-foreground">{request.client_name} - ${request.addon_price}</p>
                  <p className="text-xs text-muted-foreground">{formatTimeAgo(request.requested_at)}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Plus className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No pending requests</p>
          </div>
        );

      case 'venue-changes':
        return loadingVenueChanges ? (
          <p className="text-center text-muted-foreground py-4">Loading...</p>
        ) : pendingVenueChanges.length > 0 ? (
          <div className="space-y-3">
            {pendingVenueChanges.slice(0, 5).map((request) => (
              <div
                key={request.id}
                className="flex items-start gap-4 p-4 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors cursor-pointer border-l-4 border-emerald-500"
                onClick={() => handleVenueChangeClick(request)}
              >
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate">{request.new_venue_name}</h4>
                  <p className="text-sm text-muted-foreground">{request.client_name} - {request.event_type}</p>
                  <p className="text-xs text-muted-foreground">From: {request.current_venue_name}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No pending requests</p>
          </div>
        );

      case 'tasks':
        return loadingTasks ? (
          <p className="text-center text-muted-foreground py-4">Loading...</p>
        ) : tasksData.tasks.length > 0 ? (
          <div className="space-y-3">
            {tasksData.tasks.slice(0, 5).map((task) => (
              <div
                key={task.id}
                className={`flex items-start gap-4 p-4 rounded-xl transition-colors cursor-pointer ${
                  task.is_overdue 
                    ? 'bg-red-500/10 hover:bg-red-500/20' 
                    : task.due_date === new Date().toISOString().split('T')[0]
                      ? 'bg-orange-500/10 hover:bg-orange-500/20'
                      : 'bg-white/5 hover:bg-white/10'
                }`}
                onClick={() => navigate(`/bookings?open=${task.booking_id}&tab=tasks`)}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  task.is_overdue ? 'bg-red-500/20' : 'bg-orange-500/20'
                }`}>
                  {task.is_overdue ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <ClipboardList className="w-5 h-5 text-orange-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold truncate">{task.name}</h4>
                    <span className={`text-xs flex-shrink-0 ${task.is_overdue ? 'text-red-500' : 'text-orange-500'}`}>
                      Due: {formatDate(task.due_date)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{task.client_name || 'Unknown Client'}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
            <p className="text-muted-foreground">All caught up!</p>
          </div>
        );

      case 'unread-messages':
        return loadingMessages ? (
          <p className="text-center text-muted-foreground py-4">Loading...</p>
        ) : unreadMessages.length > 0 ? (
          <div className="space-y-3">
            {unreadMessages.slice(0, 5).map((msg) => (
              <div
                key={msg.id}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                onClick={() => navigate(`/bookings?open=${msg.booking_id}&tab=messages`)}
              >
                <div className="w-10 h-10 rounded-full bg-neon-blue/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-neon-blue" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate">{msg.client_name}</h4>
                  <p className="text-sm text-muted-foreground truncate">{msg.content}</p>
                  <p className="text-xs text-muted-foreground">{formatTimeAgo(msg.created_at)}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No unread messages</p>
          </div>
        );

      default:
        return null;
    }
  };

  // Get widget icon and color
  const getWidgetConfig = (widgetId) => {
    const configs = {
      'stats': { icon: TrendingUp, iconColor: 'text-neon-blue' },
      'upcoming-events': { icon: Calendar, iconColor: 'text-neon-purple' },
      'pending-contracts': { icon: PenTool, iconColor: 'text-orange-500' },
      'unread-emails': { icon: Mail, iconColor: 'text-blue-400' },
      'addon-requests': { icon: Plus, iconColor: 'text-purple-400' },
      'venue-changes': { icon: MapPin, iconColor: 'text-emerald-400' },
      'tasks': { icon: ClipboardList, iconColor: 'text-orange-500' },
      'unread-messages': { icon: MessageSquare, iconColor: 'text-neon-blue' },
    };
    return configs[widgetId] || { icon: FileText, iconColor: 'text-muted-foreground' };
  };

  // Get widget badge count
  const getWidgetBadge = (widgetId) => {
    const badges = {
      'pending-contracts': pendingContracts.length,
      'unread-emails': unreadEmails.length,
      'addon-requests': pendingAddonRequests.length,
      'venue-changes': pendingVenueChanges.length,
      'tasks': tasksData.overdue_count + tasksData.due_today_count,
      'unread-messages': unreadMessages.length,
    };
    return badges[widgetId] || 0;
  };

  const visibleWidgets = widgets.filter(w => w.visible);
  const hiddenWidgets = widgets.filter(w => !w.visible);

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div className="space-y-6">
        {/* Dashboard Header */}
        <div className="flex flex-col gap-4">
          {/* Title Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading font-bold" data-testid="dashboard-title">
                {isEmployee ? `Welcome, ${employeeInfo?.name || 'Team Member'}` : 'Dashboard'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
          
          {/* Controls Row - Always Visible */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white/10 rounded-xl p-1 border border-white/20">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                title="List View"
                className="flex items-center gap-2 px-3"
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
                <span className="text-sm">List</span>
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                title="Calendar View"
                className="flex items-center gap-2 px-3"
                data-testid="view-calendar-btn"
              >
                <CalendarDays className="w-4 h-4" />
                <span className="text-sm">Calendar</span>
              </Button>
            </div>
            
            {/* Customize Button */}
            <Button
              variant={isCustomizing ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIsCustomizing(!isCustomizing)}
              className={isCustomizing ? 'bg-neon-blue text-black hover:bg-neon-blue/80' : 'border-white/20 hover:bg-white/10'}
              data-testid="customize-dashboard-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              {isCustomizing ? 'Done Customizing' : 'Customize Dashboard'}
            </Button>
          </div>
        </div>

        {/* Customization Panel */}
        {isCustomizing && (
          <Card className="glass-card border-glass-border border-neon-blue/50">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <GripVertical className="w-4 h-4" />
                  Drag widgets to reorder • Click eye icon to hide
                </div>
                <div className="flex items-center gap-2">
                  {hiddenWidgets.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Hidden:</span>
                      {hiddenWidgets.map(w => (
                        <Button
                          key={w.id}
                          variant="outline"
                          size="sm"
                          onClick={() => toggleWidgetVisibility(w.id)}
                          className="text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          {w.title}
                        </Button>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={resetWidgets}>
                    Reset to Default
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleWidgets.map(w => w.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-6">
                {visibleWidgets.map((widget) => {
                  const config = getWidgetConfig(widget.id);
                  const badge = getWidgetBadge(widget.id);
                  
                  // Stats widget doesn't need collapsible wrapper
                  if (widget.id === 'stats') {
                    return (
                      <SortableWidget key={widget.id} id={widget.id} isCustomizing={isCustomizing}>
                        <Card className="glass-card border-glass-border">
                          <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="font-heading text-2xl flex items-center gap-2">
                              <TrendingUp className="w-6 h-6 text-neon-blue" />
                              Quick Stats
                            </CardTitle>
                            {isCustomizing && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleWidgetVisibility(widget.id)}
                                className="text-muted-foreground hover:text-red-400"
                              >
                                <EyeOff className="w-4 h-4" />
                              </Button>
                            )}
                          </CardHeader>
                          <CardContent>
                            {renderWidgetContent(widget.id)}
                          </CardContent>
                        </Card>
                      </SortableWidget>
                    );
                  }
                  
                  return (
                    <SortableWidget key={widget.id} id={widget.id} isCustomizing={isCustomizing}>
                      <CollapsibleCard
                        title={widget.title}
                        icon={config.icon}
                        iconColor={config.iconColor}
                        badge={badge}
                        collapsed={widget.collapsed}
                        onToggle={() => toggleWidgetCollapse(widget.id)}
                        onHide={() => toggleWidgetVisibility(widget.id)}
                        isCustomizing={isCustomizing}
                        testId={`${widget.id}-card`}
                      >
                        {renderWidgetContent(widget.id)}
                      </CollapsibleCard>
                    </SortableWidget>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </Layout>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, iconColor, label, value }) {
  return (
    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white/10`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
}
